import { useEffect, useRef, useState } from "react";
import { InfoFooter, InfoHeader } from "../components/InfoLayout";
import SlCloudShell from "../brands/servicelad/SlCloudShell";
import { RelatedFreeTools, ToolFaq, toolPageSchemas } from "../components/FreeToolSeo";
import usePageMeta from "../hooks/usePageMeta";
import infoCss from "../styles/info.css?raw";
import taxCss from "../styles/tax-calculator.css?raw";
import { formatUkReg, isValidUkReg, normalizeUkReg } from "../../lib/vehicle/regValidation";

const brandCopy = {
  serviceboss: {
    eyebrow: "Free insurance group check",
    title: "What's my car's insurance group?",
    intro: "Enter your reg to see the car's UK insurance group, 1 to 50.",
    metaTitle: "What's My Car Insurance Group? Free Checker | ServiceBoss",
    metaDescription: "What's my car's insurance group? Check for free with ServiceBoss. Enter a UK registration to see the 1-50 insurance group.",
    guideTitle: "Where the group comes from",
    guideCopy: "ServiceBoss matches your vehicle against UK insurance-group data by make, model, year and engine.",
  },
  servicebyreg: {
    eyebrow: "Free insurance group check",
    title: "What's my insurance group by registration?",
    intro: "Enter a UK reg to see its insurance group and how it compares.",
    metaTitle: "What's My Insurance Group by Reg | ServiceByReg",
    metaDescription: "What's my insurance group by registration? Check UK car insurance groups for free with ServiceByReg. See the 1-50 group in one search.",
    guideTitle: "How the match works",
    guideCopy: "ServiceByReg looks up the vehicle and matches it against published insurance-group data for the closest variant.",
  },
  servicelad: {
    eyebrow: "Free insurance group check",
    title: "What's my car's insurance group?",
    intro: "Pop in your reg to see the insurance group, from 1 to 50.",
    metaTitle: "What's My Car Insurance Group? Free Check | ServiceLad",
    metaDescription: "What's my car's insurance group? Try the free ServiceLad checker. Enter a UK reg to see the car's 1-50 insurance group.",
    guideTitle: "Where this comes from",
    guideCopy: "ServiceLad checks the vehicle against UK insurance-group data to find the closest match.",
  },
};

const insuranceFaqs = [
  ["What's my car's insurance group?", "Enter the UK registration above. We identify the vehicle and match it to published UK insurance-group data on a 1 to 50 scale."],
  ["What do insurance groups 1 to 50 mean?", "Lower groups are usually cheaper to insure. Group 1 is the lowest risk band, group 50 is the highest. The group reflects performance, repair cost and security, not your personal premium."],
  ["Is insurance group the same as my quote?", "No. The group is a reference for the vehicle. Your quote also depends on age, address, driving record, cover level and mileage."],
  ["Can I check insurance group by registration for free?", "Yes. This checker is free, with no account. Coverage is strongest for cars sold in the UK since around 2010."],
  ["Why might no group show for my car?", "Some rare trims, older vehicles or incomplete public records do not match cleanly. If that happens, we say so clearly instead of guessing."],
];

function GroupResult({ data, brand }) {
  const { vehicle, group } = data;

  return (
    <section className="tax-result" aria-live="polite">
      <div className="tax-result-head">
        <div>
          <span className="tax-result-kicker">Vehicle found</span>
          <h2>{vehicle.make} {vehicle.model}</h2>
        </div>
        <div className="tax-result-plate"><span>UK</span><strong>{formatUkReg(vehicle.registration)}</strong></div>
      </div>

      <div className="tax-rate-label">Insurance group</div>
      <div className="tax-rate-grid">
        <article className="tax-rate-card is-primary">
          <span>Group</span>
          <strong>{group} / 50</strong>
        </article>
      </div>

      <div className="tax-detail-grid">
        <div className="tax-detail"><span>Make</span><strong>{vehicle.make || "Not available"}</strong></div>
        <div className="tax-detail"><span>Model</span><strong>{vehicle.model || "Not available"}</strong></div>
        <div className="tax-detail"><span>Year</span><strong>{vehicle.year || "Not available"}</strong></div>
      </div>

      <div className="tax-disclaimer">
        <strong>Not a quotation.</strong>
        <span>This is a reference group from published data, not an official manufacturer figure or an insurance quote. Your actual premium depends on the driver, address, cover, mileage and claims history too. A {brand.name} report can help you check the vehicle's own history alongside this.</span>
      </div>
    </section>
  );
}

function NotFound({ reason }) {
  return (
    <div className="tax-unknown">
      <span>No group found</span>
      <h3>We couldn't match this vehicle to a group.</h3>
      <p>{reason || "This variant isn't in our current insurance-group data. Coverage is strongest for cars sold in the UK since around 2010."}</p>
    </div>
  );
}

export default function InsuranceGroupPage({ brand }) {
  const copy = brandCopy[brand.key] || brandCopy.serviceboss;
  const initialRegistration = normalizeUkReg(new URLSearchParams(window.location.search).get("reg"));
  const [registration, setRegistration] = useState(initialRegistration);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const autoLookupDone = useRef(false);
  const canonical = `https://${brand.domain}/insurance-groups`;

  usePageMeta({ title: copy.metaTitle, description: copy.metaDescription, canonical });

  const runLookup = async (value) => {
    const clean = normalizeUkReg(value);
    if (!isValidUkReg(clean)) {
      setError("Check the reg and try again.");
      setData(null);
      return;
    }

    setLoading(true);
    setError("");
    setData(null);
    try {
      const response = await fetch(`/api/insurance-group-check?reg=${encodeURIComponent(clean)}`, { headers: { Accept: "application/json" } });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || "The vehicle could not be checked.");
      setData(result);
      const resultPath = result.page_url
        ? new URL(result.page_url).pathname
        : `/insurance-groups?reg=${encodeURIComponent(clean)}`;
      window.history.replaceState(null, "", resultPath);
    } catch (lookupError) {
      setError(lookupError.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!autoLookupDone.current && isValidUkReg(initialRegistration)) {
      autoLookupDone.current = true;
      runLookup(initialRegistration);
    }
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@graph": toolPageSchemas({
      brand,
      canonical,
      name: `What's My Car Insurance Group? ${brand.name}`,
      description: copy.metaDescription,
      alternateName: [
        "Insurance group checker",
        "Car insurance group checker",
        "What insurance group is my car",
        "Insurance group by registration",
        "ABI insurance group check",
      ],
      pathLabel: "Insurance group checker",
    }),
  };

  return (
    <SlCloudShell brand={brand}>
      <style>{infoCss}</style>
      <style>{taxCss}</style>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      <InfoHeader brand={brand} />
      <main className="tax-main">
        <section className="tax-hero">
          <div className="wrap tax-hero-grid">
            <div className="tax-hero-copy">
              <span className="tax-eyebrow">{copy.eyebrow}</span>
              <h1>{copy.title}</h1>
              <p>{copy.intro}</p>
              <div className="tax-proof"><span>Free to use</span><span>Trusted UK data</span><span>No payment needed</span></div>
            </div>

            <form className="tax-form-card" onSubmit={(event) => { event.preventDefault(); runLookup(registration); }} noValidate>
              <label htmlFor="ig-registration">Enter your reg</label>
              <div className="tax-plate-input">
                <span className="tax-plate-band" aria-hidden="true"><img src="/gb-flag.svg" alt="" />UK</span>
                <input
                  id="ig-registration"
                  value={registration}
                  maxLength="8"
                  autoComplete="off"
                  inputMode="text"
                  placeholder="ENTER REG"
                  aria-describedby="ig-form-help ig-form-error"
                  onChange={(event) => {
                    setRegistration(normalizeUkReg(event.target.value));
                    setError("");
                  }}
                />
              </div>
              <button type="submit" disabled={loading}>{loading ? "Checking your vehicle..." : "Check my insurance group"}</button>
              <small id="ig-form-help">We only use your reg to check the vehicle with DVLA and DVSA.</small>
              <div className="tax-form-error" id="ig-form-error" role="alert">{error}</div>
            </form>
          </div>
        </section>

        <div className="wrap tax-page-body">
          {loading && <div className="tax-loading" aria-live="polite"><span /><p>Checking your vehicle...</p></div>}
          {data?.found && <GroupResult data={data} brand={brand} />}
          {data && !data.found && <NotFound reason={data.reason} />}

          <section className="tax-explainer">
            <div>
              <span className="tax-section-kicker">How it works</span>
              <h2>{copy.guideTitle}</h2>
              <p>{copy.guideCopy}</p>
            </div>
            <ol>
              <li><span>1</span><div><strong>Enter your reg</strong><p>We check that it looks like a UK number plate.</p></div></li>
              <li><span>2</span><div><strong>We identify the vehicle</strong><p>Make, model, year and engine come from DVLA and DVSA records.</p></div></li>
              <li><span>3</span><div><strong>We match the group</strong><p>Your vehicle is matched against the closest recorded variant.</p></div></li>
            </ol>
          </section>

          <section className="tax-scope">
            <div><img src="/svgs/taxprice.svg" alt="" /><h2>What the insurance group means</h2></div>
            <ul>
              <li>1 to 50 scale, lower is cheaper to insure</li>
              <li>Based on performance, repair cost and security</li>
              <li>Not the same as your personal premium</li>
              <li>Free to check, no account needed</li>
            </ul>
            <p>Groups come from a mix of published sources and can vary by exact trim, engine output and options fitted.</p>
          </section>

          <ToolFaq title="Common insurance group questions" items={insuranceFaqs} />
          <RelatedFreeTools current="insurance" />
        </div>
      </main>
      <InfoFooter brand={brand} />
    </SlCloudShell>
  );
}
